import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  FaComments,
  FaEnvelope,
  FaGraduationCap,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaPhone,
  FaSearch,
  FaUserPlus,
} from 'react-icons/fa';
import { playerAPI } from '../../services/api';
import { publicAssetUrl } from '../../utils/publicAssetUrl';
import { getConsolidatedCantonsForRegistration } from '../../utils/cantons';
import { SKILL_RANGE_OPTIONS } from '../../constants/playerSignup';
import PlayerDmChatModal from '../PlayerDmChatModal';
import InvitePlayerModal from '../playerInvites/InvitePlayerModal';
import RequestJoinToPlayerModal from '../playerInvites/RequestJoinToPlayerModal';
import ProfilePublicToggle from '../playerProfile/ProfilePublicToggle';

const Wrap = styled.div`
  width: 100%;
`;

const Caption = styled.p`
  font-size: 0.82rem;
  color: #64748b;
  margin: 0 0 0.85rem;
  line-height: 1.45;
`;

const ProfileVisibilityBlock = styled.div`
  margin-bottom: 1rem;
`;

const ProfileVisibilityHint = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.78rem;
  color: #64748b;
  line-height: 1.45;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-bottom: 1rem;
  align-items: flex-end;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  min-width: 120px;
`;

const Input = styled.input`
  padding: 0.5rem 0.65rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.88rem;
`;

const Select = styled.select`
  padding: 0.5rem 0.65rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.88rem;
  background: #fff;
`;

const SearchBtn = styled.button`
  padding: 0.52rem 1rem;
  border: none;
  border-radius: 8px;
  background: #043663;
  color: #fff;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
`;

const Card = styled.article`
  background: #fff;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 16px;
  padding: 1rem 1.05rem;
`;

const CardTop = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 0.65rem;
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #e2e8f0;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #475569;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Name = styled.div`
  font-weight: 700;
  color: #0f172a;
  font-size: 1rem;
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: #64748b;
  margin-bottom: 0.75rem;
`;

const MetaLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const ContactLink = styled.a`
  color: #043663;
  font-weight: 600;
  text-decoration: none;
  word-break: break-all;
  &:hover {
    text-decoration: underline;
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

const ActionBtn = styled.button`
  padding: 0.42rem 0.65rem;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$primary ? '#043663' : '#cbd5e1')};
  background: ${(p) => (p.$primary ? '#043663' : '#fff')};
  color: ${(p) => (p.$primary ? '#fff' : '#334155')};
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Empty = styled.p`
  text-align: center;
  color: #94a3b8;
  padding: 2rem 1rem;
  font-size: 0.9rem;
`;

/**
 * @param {object} [props]
 * @param {boolean} [props.embedded]
 */
export default function OpenPlayersDirectory({ embedded = false }) {
  const { t } = useTranslation();
  const [canton, setCanton] = useState('');
  const [nameQ, setNameQ] = useState('');
  const [skillRange, setSkillRange] = useState('');
  const [searchParams, setSearchParams] = useState(null);
  const [dmModal, setDmModal] = useState(null);
  const [inviteTarget, setInviteTarget] = useState(null);
  const [joinTarget, setJoinTarget] = useState(null);

  const { data: myTeamsRes } = useQuery(['playerTeams'], () => playerAPI.getTeams(), {
    staleTime: 60 * 1000,
  });
  const myTeams = Array.isArray(myTeamsRes?.data) ? myTeamsRes.data : myTeamsRes?.data?.data || [];

  const invitableTeams = useMemo(
    () =>
      myTeams
        .filter(
          (row) =>
            row.canManageJoinRequests &&
            (row.activeMemberCount == null || Number(row.activeMemberCount) < 2)
        )
        .map((row) => ({ teamId: row.teamId, teamName: row.teamName })),
    [myTeams]
  );

  const { data: profileRes, isLoading: profileLoading } = useQuery(
    ['playerProfile'],
    () => playerAPI.getProfile(),
    { staleTime: 60 * 1000 }
  );
  const profile = profileRes?.data;
  const profilePublic = profile?.profilePublic === true;

  const cantonOptions = getConsolidatedCantonsForRegistration();

  const { data, isLoading, isFetching, refetch } = useQuery(
    ['openPlayers', searchParams],
    () => playerAPI.getOpenPlayers(searchParams || {}),
    { enabled: searchParams !== null, staleTime: 30 * 1000 }
  );

  const players = Array.isArray(data?.data) ? data.data : [];

  const messageMutation = useMutation(
    ({ playerId, playerName }) => playerAPI.getPlayerDirectoryConversation(playerId),
    {
      onSuccess: (res, variables) => {
        const cid = res?.data?.conversationId;
        if (!cid) {
          toast.error(t('playerTeamsPage.toastChatFailed'));
          return;
        }
        setDmModal({
          conversationId: cid,
          title: variables.playerName || t('playerTeamsPage.playerFallback'),
          subtitle: t('playerTeamsPage.directoryChatSubtitle'),
        });
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || t('playerTeamsPage.toastChatFailed'));
      },
    }
  );

  const runSearch = () => {
    setSearchParams({
      canton: canton.trim(),
      q: nameQ.trim(),
      ...skillParamsFromRange(skillRange),
    });
    refetch();
  };

  return (
    <Wrap>
      {!embedded && <Caption>{t('playerTeamsPage.findTeammatesCaption')}</Caption>}
      <ProfileVisibilityBlock>
        <ProfilePublicToggle
          profilePublic={profilePublic}
          disabled={profileLoading || !profile}
        />
        <ProfileVisibilityHint>{t('phase2.playerProfile.hints.profilePublic')}</ProfileVisibilityHint>
      </ProfileVisibilityBlock>
      <FilterRow>
        <Field>
          {t('playerTeamsPage.filterCanton')}
          <Select value={canton} onChange={(e) => setCanton(e.target.value)}>
            <option value="">{t('playerTeamsPage.filterCantonPlaceholder')}</option>
            {cantonOptions.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label || c.code}
              </option>
            ))}
          </Select>
        </Field>
        <Field>
          {t('playerTeamsPage.filterPlayerName')}
          <Input
            value={nameQ}
            onChange={(e) => setNameQ(e.target.value)}
            placeholder={t('playerTeamsPage.filterPlayerNamePlaceholder')}
          />
        </Field>
        <Field>
          {t('playerTeamsPage.filterSkill')}
          <Select value={skillRange} onChange={(e) => setSkillRange(e.target.value)}>
            <option value="">{t('playerTeamsPage.filterSkillAny')}</option>
            {SKILL_RANGE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Select>
        </Field>
        <SearchBtn type="button" onClick={runSearch} disabled={isLoading || isFetching}>
          <FaSearch style={{ marginRight: 6 }} aria-hidden />
          {isLoading || isFetching ? t('playerTeamsPage.searchLoading') : t('playerTeamsPage.search')}
        </SearchBtn>
      </FilterRow>

      {searchParams === null ? (
        <Empty>{t('playerTeamsPage.findTeammatesSearchPrompt')}</Empty>
      ) : isLoading ? (
        <Empty>{t('playerTeamsPage.loadingOpenPlayers')}</Empty>
      ) : players.length === 0 ? (
        <Empty>{t('playerTeamsPage.noOpenPlayers')}</Empty>
      ) : (
        <Grid>
          {players.map((p) => {
            const name = p.fullName || [p.firstName, p.lastName].filter(Boolean).join(' ');
            const img = p.profilePictureUrl ? publicAssetUrl(p.profilePictureUrl) : '';
            const email = p.email && String(p.email).trim();
            const phone = p.phoneNumber && String(p.phoneNumber).trim();
            return (
              <Card key={p.id}>
                <CardTop>
                  <Avatar>
                    {img ? <img src={img} alt="" loading="lazy" /> : initials(name)}
                  </Avatar>
                  <div>
                    <Name>{name || '—'}</Name>
                  </div>
                </CardTop>
                <Meta>
                  <MetaLine>
                    <FaMapMarkerAlt size={12} aria-hidden />
                    {p.homeCanton || '—'}
                  </MetaLine>
                  <MetaLine>
                    <FaGraduationCap size={12} aria-hidden />
                    {t('playerTeamsPage.skillPrefix')}{' '}
                    {p.skillLevel != null ? p.skillLevel : '—'}
                  </MetaLine>
                  <MetaLine>
                    <FaEnvelope size={12} aria-hidden />
                    {email ? (
                      <ContactLink href={`mailto:${email}`}>{email}</ContactLink>
                    ) : (
                      '—'
                    )}
                  </MetaLine>
                  <MetaLine>
                    <FaPhone size={12} aria-hidden />
                    {phone ? <ContactLink href={`tel:${phone}`}>{phone}</ContactLink> : '—'}
                  </MetaLine>
                </Meta>
                <ActionRow>
                  <ActionBtn
                    type="button"
                    $primary
                    disabled={messageMutation.isLoading}
                    onClick={() =>
                      messageMutation.mutate({
                        playerId: p.id,
                        playerName: name || t('playerTeamsPage.playerFallback'),
                      })
                    }
                  >
                    <FaComments aria-hidden />
                    {t('playerTeamsPage.messagePlayer')}
                  </ActionBtn>
                  {invitableTeams.length > 0 ? (
                    <ActionBtn
                      type="button"
                      onClick={() =>
                        setInviteTarget({ id: p.id, name: name || t('playerTeamsPage.playerFallback') })
                      }
                    >
                      <FaPaperPlane aria-hidden />
                      {t('playerTeamsPage.invitePlayer')}
                    </ActionBtn>
                  ) : null}
                  <ActionBtn
                    type="button"
                    onClick={() =>
                      setJoinTarget({ id: p.id, name: name || t('playerTeamsPage.playerFallback') })
                    }
                  >
                    <FaUserPlus aria-hidden />
                    {t('playerTeamsPage.requestJoinPlayer')}
                  </ActionBtn>
                </ActionRow>
              </Card>
            );
          })}
        </Grid>
      )}

      {dmModal ? (
        <PlayerDmChatModal
          isOpen={!!dmModal.conversationId}
          conversationId={dmModal.conversationId}
          title={dmModal.title}
          subtitle={dmModal.subtitle}
          onClose={() => setDmModal(null)}
        />
      ) : null}

      {inviteTarget ? (
        <InvitePlayerModal
          invitableTeams={invitableTeams}
          invitedPlayerId={inviteTarget.id}
          invitedPlayerName={inviteTarget.name}
          onClose={() => setInviteTarget(null)}
        />
      ) : null}

      {joinTarget ? (
        <RequestJoinToPlayerModal
          targetPlayerId={joinTarget.id}
          targetPlayerName={joinTarget.name}
          onClose={() => setJoinTarget(null)}
        />
      ) : null}
    </Wrap>
  );
}

function initials(name) {
  if (!name) return '?';
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length >= 2) return (p[0][0] + p[p.length - 1][0]).toUpperCase();
  return (name[0] || '?').toUpperCase();
}

function skillParamsFromRange(skillRange) {
  if (!skillRange) return {};
  const [min, max] = String(skillRange).split('-').map(Number);
  if (Number.isNaN(min) || Number.isNaN(max)) return {};
  return { skillMin: min, skillMax: max };
}
